import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

import { OrgSwitcher } from '@/components/layout/OrgSwitcher'
import { SidebarNav } from '@/components/layout/SidebarNav'
import { useFocusTrap } from '@/lib/keyboard/use-focus-trap'
import { cn } from '@/lib/utils'

export type SidebarMode = 'expanded' | 'rail' | 'drawer'

const SIDEBAR_WIDTH_KEY = 'cbs.sidebar.width'
const SIDEBAR_MIN = 208
const SIDEBAR_MAX = 420
const SIDEBAR_DEFAULT = 256

function clampWidth(value: number): number {
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, value))
}

/** Persisted, drag-adjustable width for the expanded rail. */
function useResizableWidth() {
  const [width, setWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY)
      return stored ? clampWidth(Number(stored)) : SIDEBAR_DEFAULT
    } catch {
      return SIDEBAR_DEFAULT
    }
  })
  const dragging = useRef(false)

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    event.preventDefault()
    dragging.current = true
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }, [])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!dragging.current) return
      setWidth(clampWidth(event.clientX))
    }
    const onUp = () => {
      if (!dragging.current) return
      dragging.current = false
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      setWidth((w) => {
        try {
          localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w))
        } catch {
          // best effort
        }
        return w
      })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [])

  return { width, onPointerDown }
}

export interface SidebarProps {
  /** `expanded` ≥1024px, `rail` 768–1023px, `drawer` below that. */
  mode: SidebarMode
  /** Drawer only — ignored in the other two modes. */
  open?: boolean | undefined
  onOpenChange?: ((open: boolean) => void) | undefined
}

/**
 * The left rail.
 *
 * It scrolls independently of the content so a long nav never drags the page
 * with it, and the header (wordmark + org) stays pinned while the nav scrolls.
 */
export function Sidebar({ mode, open = false, onOpenChange }: SidebarProps) {
  const panelRef = useRef<HTMLElement>(null)
  const isDrawer = mode === 'drawer'
  const collapsed = mode === 'rail'
  const { width, onPointerDown } = useResizableWidth()
  // Only the always-on expanded rail is user-resizable; the rail is fixed-narrow
  // and the drawer is a full overlay.
  const resizable = mode === 'expanded'

  const close = () => onOpenChange?.(false)

  // The drawer covers the page, so it behaves like a dialog: focus is trapped
  // inside it and returns to the hamburger when it closes.
  useFocusTrap(isDrawer && open, panelRef, close)

  const panel = (
    <aside
      ref={panelRef}
      aria-label="Sidebar"
      style={resizable ? { width } : undefined}
      className={cn(
        'relative flex h-full shrink-0 flex-col overflow-y-auto border-r border-border bg-sidebar',
        collapsed ? 'w-sidebar-collapsed' : !resizable && 'w-sidebar',
        isDrawer && 'fixed inset-y-0 left-0 z-50 shadow-xl',
      )}
    >
      {resizable && (
        <div
          onPointerDown={onPointerDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          className="group/resize absolute inset-y-0 right-0 z-10 w-1.5 cursor-col-resize"
        >
          <div className="absolute inset-y-0 right-0 w-px bg-transparent transition-colors group-hover/resize:bg-accent" />
        </div>
      )}
      {/* No wordmark: the top holds only the current company and its switcher,
          so the nav gets the space instead. */}
      <div className={cn('flex items-center gap-2 p-3', collapsed && 'justify-center')}>
        <div className="min-w-0 flex-1">
          <OrgSwitcher collapsed={collapsed} />
        </div>
        {isDrawer && (
          <button
            type="button"
            onClick={close}
            aria-label="Close navigation"
            className="shrink-0 rounded-control p-2 text-fg-secondary transition-colors hover:bg-sidebar-hover hover:text-fg"
          >
            <X className="size-4" aria-hidden />
          </button>
        )}
      </div>

      <div className="flex-1 pb-4">
        {/* Navigating from the drawer must dismiss it — the destination is
            underneath it. */}
        <SidebarNav collapsed={collapsed} onNavigate={isDrawer ? close : undefined} />
      </div>
    </aside>
  )

  if (!isDrawer) return panel

  if (!open) return null

  return (
    <>
      {/* Presentational: Esc and the close button are the keyboard routes out. */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[3px]"
        aria-hidden
        onClick={close}
      />
      {panel}
    </>
  )
}
